import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

import WeatherWidget, { CurrentWeather } from './components/WeatherWidget';
import { DailyForecast } from './components/ForecastDaily';
import Loader, { LoaderAnimationState } from './components/Loader';
import ErrorComponent from './components/Error';
import { rAFTimeout } from '../../utils/utils';

interface WeatherData {
  current: CurrentWeather;
  forecast: Array<DailyForecast>;
}

interface CachedWeatherInfo {
  timestamp: number;
  data: WeatherData;
}

async function fetchWeatherData(cityId: number, units: string, lang: string): Promise<WeatherData> {
  const resp = await fetch(
    `/.netlify/functions/get-weather-forecast?cityId=${cityId}&units=${units}&lang=${lang}`,
  );
  if (resp.status !== 200) {
    throw new Error('Got response with status code ' + resp.status);
  }
  return await resp.json();
}

interface WeatherWidgetContainerProps {
  cities: Array<{ name: string; id: number; default?: true }>;
  units: string;
  lang: string;
}

const WeatherWidgetContainer = ({
  cities,
  units,
  lang,
}: WeatherWidgetContainerProps): React.ReactElement => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [widgetState, setWidgetState] = useState<LoaderAnimationState | 'error'>('pending');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const [currentCityId, setCurrentCityId] = useState(cities.find(city => city.default)!.id);

  useEffect(() => {
    async function init() {
      try {
        setWidgetState('animatingIn');
        const cacheKey = `weatherForecast-${currentCityId}`;

        let weatherData: WeatherData | null = null;

        if (window.localStorage) {
          const cachedWeatherInfoJSON = window.localStorage.getItem(cacheKey);
          if (cachedWeatherInfoJSON !== null) {
            const cachedWeatherInfo: CachedWeatherInfo = JSON.parse(cachedWeatherInfoJSON);
            const minsDiff = (Date.now() - cachedWeatherInfo.timestamp) / (60 * 1000);
            if (minsDiff <= 10) {
              weatherData = cachedWeatherInfo.data;
            }
          }
        }

        if (!weatherData) {
          weatherData = await fetchWeatherData(currentCityId, units, lang);
          const weatherInfo: CachedWeatherInfo = {
            timestamp: Date.now(),
            data: weatherData,
          };
          if (window.localStorage) {
            window.localStorage.setItem(cacheKey, JSON.stringify(weatherInfo));
          }
        }

        rAFTimeout(() => {
          setWidgetState('animatingOut');
          rAFTimeout(() => setWeatherData(weatherData), 600);
        }, 1000);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        rAFTimeout(() => {
          setWidgetState('animatingOut');
          rAFTimeout(() => setWidgetState('error'), 600);
        }, 1000);
      }
    }
    init();
  }, [currentCityId]);

  if (widgetState === 'error') {
    return <ErrorComponent />;
  }

  return widgetState === 'animatingOut' && weatherData ? (
    <WeatherWidget
      cities={cities}
      currentCityId={currentCityId}
      onCitySelect={cityId => setCurrentCityId(cityId)}
      currentCondition={weatherData.current}
      forecastDaily={weatherData.forecast}
    />
  ) : (
    <Loader animationState={widgetState} />
  );
};

export default WeatherWidgetContainer;
